import { useState } from 'react';
import toast from 'react-hot-toast';
import { jobAPI } from '../../../services/api';

// Extracted verbatim from JobManagement.js (Task 5).
//
// `isLoading` stays owned by the orchestrator — this hook receives
// #52: no longer takes `setIsLoading` — updateJobStatus runs inside an overlay.
//
// `updateJobStatus` used to call `loadJobs()` directly on success. That call is
// inverted into the `onStatusUpdated` callback so this action hook does not
// depend on `useJobs`.
//
// Coupling note (reported, not improvised around): two dependencies fall
// outside the shape the plan documented for this hook.
//   - `selectedJobForActions` is the job being updated. It is orchestrator-owned
//     (the actions dropdown and three other flows read it), so it is threaded in
//     as a value rather than duplicated here.
//   - `setShowStatusModal` gates a modal rendered by the orchestrator.
//     `closeStatusModal` is the only thing in this hook that touches it.
// Both are passed as constructor arguments, the same pattern already used for
// `setIsLoading` and for `useCustomers`'s modal setters in Task 4.
//
// `showStatusUpdateModal` deliberately stays in the orchestrator: it seeds this
// hook's form via the exported `setStatusUpdateForm`, but it also sets
// `selectedJobForActions`, which this hook does not own.
export const useJobStatus = ({
  onStatusUpdated,
  selectedJobForActions,
  setShowStatusModal
}) => {
  // Status update form state
  // #52: updateJobStatus runs from inside the status overlay, whose trigger is
  // a row action in the jobs table. JobsTab passes the shared `isLoading` to
  // DataTable as `loading`, so flipping it here unmounted that row mid-overlay
  // and left focus with nowhere to return on close. Overlay-scoped instead.
  const [overlayLoading, setOverlayLoading] = useState(false);
  const [statusUpdateForm, setStatusUpdateForm] = useState({
    status: 'created',
    completion_date: '',
    notify_customer: false,
    customer_notes: ''
  });

  // Set when a completed job's response carries a pay link or a warning: the
  // modal then stays open on that result until the mechanic dismisses it.
  const [payResult, setPayResult] = useState(null);

  const closeStatusModal = () => {
    setPayResult(null);
    setShowStatusModal(false);
    // Don't clear selectedJobForActions to keep the actions dropdown open
  };

  const updateJobStatus = async () => {
    try {
      console.log('Updating job with data:', selectedJobForActions.id, statusUpdateForm);
      setOverlayLoading(true);
      const result = await jobAPI.updateJobStatus(selectedJobForActions.id, statusUpdateForm);

      let successMsg = `Job status updated to ${statusUpdateForm.status.replace(/_/g, ' ')}`;
      // customer_notified means the email was queued (job_routes.py sends it in a
      // background task), not delivered, so the toast says no more than that.
      if (result.customer_notified) {
        successMsg += `. Sending an email to ${result.customer_email}`;
      }
      toast.success(successMsg);

      if (result.pay_link_url || result.pay_warning) {
        setPayResult({
          url: result.pay_link_url || '',
          balance: result.balance_due,
          warning: result.pay_warning || ''
        });
      } else {
        closeStatusModal();
      }
      // Force refresh the jobs list to show updated data
      await onStatusUpdated();
    } catch (error) {
      console.error('Error updating job status:', error);
      toast.error(`Error updating job status: ${error.message || 'Unknown error'}`);
    } finally {
      setOverlayLoading(false);
    }
  };

  return {
    statusOverlayLoading: overlayLoading,
    statusUpdateForm,
    setStatusUpdateForm,
    payResult,
    updateJobStatus,
    closeStatusModal
  };
};
